import { clearAssistance, getAssistance, saveAssistance } from "./admin-assistance";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  authApi,
  assistanceApi,
  getHomePath,
  invalidateOfflineState,
  cancelInFlightApiRequests,
  type AuthUser,
  type ProviderRegistrationInput,
  type ProviderRegistrationResubmissionInput,
  type SessionResponse,
  type VerificationPendingResponse,
} from "./api";
import {
  setOfflineIdentity,
  getOfflineIdentityScope,
  getOfflineSessionRevision,
  subscribeToOfflineStore,
} from "./offline-store";

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  homePath: string;
  refresh: () => Promise<void>;
  registerProvider: (
    payload: ProviderRegistrationInput,
  ) => Promise<SessionResponse | VerificationPendingResponse>;
  resubmitProviderRegistration: (
    payload: ProviderRegistrationResubmissionInput,
  ) => Promise<SessionResponse>;
  signIn: (email: string, password: string) => Promise<SessionResponse>;
  signOut: () => Promise<void>;
  verifyEmail: (payload: {
    email: string;
    code: string;
    booking_draft_id?: string | null;
  }) => Promise<SessionResponse>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function anonymousState() {
  return {
    status: "anonymous" as const,
    user: null,
    homePath: "/sign-in",
  };
}

export function AuthProvider({ children }: {
  children: ReactNode;
}) {
  const refreshGeneration = useRef(0);
  const updatingLocalSession = useRef(false);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [homePath, setHomePath] = useState(() => anonymousState().homePath);

  const applySession = useCallback((session: SessionResponse) => {
    refreshGeneration.current += 1;
    updatingLocalSession.current = true;
    try {
      const identity = getAssistance()?.actor_user_id ?? session.user.id;
      if (getOfflineIdentityScope() !== encodeURIComponent(identity)) {
        invalidateOfflineState();
      }
      setOfflineIdentity(identity);
      setStatus("authenticated");
      setUser(session.user);
      setHomePath(session.home_path);
    } finally {
      updatingLocalSession.current = false;
    }
  }, []);

  const clearSession = useCallback(() => {
    refreshGeneration.current += 1;
    updatingLocalSession.current = true;
    try {
      invalidateOfflineState();
      const next = anonymousState();
      setStatus(next.status);
      setUser(next.user);
      setHomePath(next.homePath);
    } finally {
      updatingLocalSession.current = false;
    }
  }, []);

  const refresh = useCallback(async () => {
    const generation = ++refreshGeneration.current;
    try {
      const assistance = getAssistance();
      if (assistance) {
        try {
          const session = await assistanceApi.current();
          if (generation !== refreshGeneration.current) return;
          saveAssistance({ ...assistance, ...session });
          applySession(session);
          return;
        } catch {
          if (generation !== refreshGeneration.current) return;
          clearAssistance();
          cancelInFlightApiRequests();
        }
      }
      const status = await authApi.status();
      if (generation !== refreshGeneration.current) return;
      if (status.authenticated && status.user) {
        applySession({
          user: status.user,
          home_path: status.home_path ?? getHomePath(status.user.role),
        });
        return;
      }

      // /auth/status answers 200-unauthenticated when the short-lived
      // session cookie is gone — the 30d refresh cookie may still be
      // valid, so try one rotation before dropping to anonymous.
      try {
        const refreshed = await authApi.refresh();
        if (generation !== refreshGeneration.current) return;
        if (refreshed.authenticated && refreshed.user) {
          applySession({
            user: refreshed.user,
            home_path: refreshed.home_path ?? getHomePath(refreshed.user.role),
          });
          return;
        }
      } catch {
        if (generation !== refreshGeneration.current) return;
        // Refresh rejected — the session is dead; fall through.
      }

      clearSession();
    } catch {
      if (generation !== refreshGeneration.current) return;
      clearSession();
    }
  }, [applySession, clearSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    let observedRevision = getOfflineSessionRevision().revision;
    return subscribeToOfflineStore(() => {
      const nextRevision = getOfflineSessionRevision().revision;
      if (nextRevision === observedRevision) return;
      observedRevision = nextRevision;
      if (updatingLocalSession.current) return;
      cancelInFlightApiRequests();
      void refresh();
    });
  }, [refresh]);

  useEffect(() => () => {
    refreshGeneration.current += 1;
    cancelInFlightApiRequests();
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const session = await authApi.login({ email, password });
      applySession(session);
      return session;
    },
    [applySession],
  );

  const registerProvider = useCallback(
    async (payload: ProviderRegistrationInput) => {
      const result = await authApi.registerProvider(payload);
      if ("user" in result) {
        applySession(result);
      }
      return result;
    },
    [applySession],
  );

  const resubmitProviderRegistration = useCallback(
    async (payload: ProviderRegistrationResubmissionInput) => {
      const session = await authApi.resubmitProviderRegistration(payload);
      applySession(session);
      return session;
    },
    [applySession],
  );

  const verifyEmail = useCallback(
    async (payload: { email: string; code: string; booking_draft_id?: string | null }) => {
      const session = await authApi.verifyEmail(payload);
      applySession(session);
      return session;
    },
    [applySession],
  );

  const signOut = useCallback(async () => {
    if (getAssistance()) {
      try { await assistanceApi.end(); } catch { /* Parent logout below also revokes assistance. */ } finally { clearAssistance(); }
    }
    clearSession();
    try {
      await authApi.logout();
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo(
    () => ({
      status,
      user,
      homePath,
      refresh,
      registerProvider,
      resubmitProviderRegistration,
      signIn,
      signOut,
      verifyEmail,
    }),
    [
      homePath,
      refresh,
      registerProvider,
      resubmitProviderRegistration,
      signIn,
      signOut,
      status,
      user,
      verifyEmail,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return value;
}
